/**
 * The client half of the CV write contract (audit F04, B02; server half is
 * B01, docs/planning/cv-spine/CONTRACT.md invariant I6).
 *
 * Every CV content write the editor makes states the revision its draft is
 * based on (`If-Match: "<revision>"`). The server compares and swaps; a stale
 * write is answered 409 CV_REVISION_CONFLICT with `meta.currentRevision` and
 * nothing is written. This module decides which revision a write states and
 * what happens on a conflict:
 *
 * - Writes to one CV run one at a time, in order. Each states the revision the
 *   previous write returned, so an editor never conflicts with itself (the
 *   main editor saves every role as its own request; the style panel saves
 *   every 300 ms). A newer pending write with the same `coalesceKey` replaces
 *   an older one that has not been sent yet (newest full config wins).
 * - A sheet's draft keeps the revision it was opened with (a lease). Reads
 *   that arrive later (a refetch that brought in another tab's edit) do not
 *   move it, so saving that draft is a conflict instead of a silent overwrite.
 *   This client's own writes do move it, so other surfaces of the same editor
 *   never trigger a false conflict.
 * - On a conflict the user's draft is never touched: the latest CV is fetched,
 *   then ONE prompt asks to apply the change on top (the same patch is resent
 *   on the new revision), discard it (nothing more is written) or keep editing
 *   (nothing is written; the draft stays where it is).
 *
 * Pure: no React, no React Native. The query cache, the refetch and the
 * dialog are injected (see hooks/useCvWriteRunner.ts).
 */

export const CV_REVISION_CONFLICT_CODE = 'CV_REVISION_CONFLICT';

/** What the conflict prompt explains (see lib/cv-write-policy.ts). */
export type CvWriteKind = 'field' | 'section' | 'config';
export type CvConflictChoice = 'apply' | 'discard' | 'keep';
export type CvChangeOutcome = 'discarded' | 'kept';

/** The server refused a write based on an older revision (409). */
export class CvRevisionConflictError extends Error {
  readonly code = CV_REVISION_CONFLICT_CODE;

  constructor(
    message: string,
    readonly currentRevision?: number,
  ) {
    super(message);
    this.name = 'CvRevisionConflictError';
  }
}

/**
 * The user answered a conflict with "discard" or "keep editing": nothing of
 * this save was written. Forms keep the draft for "kept" and close for
 * "discarded"; neither is a failure to report.
 */
export class CvChangeNotAppliedError extends Error {
  readonly code = 'CV_CHANGE_NOT_APPLIED';

  constructor(readonly outcome: CvChangeOutcome) {
    super(
      outcome === 'discarded'
        ? 'The change was discarded; the CV keeps the version saved elsewhere.'
        : 'The change was not saved yet; it is still in the editor.',
    );
    this.name = 'CvChangeNotAppliedError';
  }
}

export function isCvRevisionConflict(error: unknown): error is CvRevisionConflictError {
  if (error instanceof CvRevisionConflictError) return true;
  return (
    !!error &&
    typeof error === 'object' &&
    (error as { code?: unknown }).code === CV_REVISION_CONFLICT_CODE
  );
}

/** The user's conflict choice carried by a failed save, or null. */
export function cvChangeNotApplied(error: unknown): CvChangeOutcome | null {
  return error instanceof CvChangeNotAppliedError ? error.outcome : null;
}

/**
 * The settled results of one editor save that wrote several items (the main
 * editor saves each role, school or certificate as its own request). A
 * conflict choice is reported as such; otherwise today's rule: only a save
 * where every item failed is a failure.
 */
export function assertCvItemBatchSaved(results: PromiseSettledResult<unknown>[]): void {
  const notApplied = results.find(
    (result): result is PromiseRejectedResult =>
      result.status === 'rejected' && cvChangeNotApplied(result.reason) !== null,
  );
  if (notApplied) throw notApplied.reason;
  const failed = results.filter((result) => result.status === 'rejected').length;
  if (failed > 0 && failed === results.length) {
    throw new Error('Failed to save');
  }
}

/** A revision as the API states it: a non-negative integer, else undefined. */
export function readCvRevision(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? value
    : undefined;
}

/**
 * The revision a CV response carries: `meta.revision` (profile routes,
 * overrides, resolved), else the row's `revision` (PATCH /api/cv/:id and
 * /overrides), else the resolved document's `cv.revision`.
 */
export function readResponseRevision(response: {
  data?: unknown;
  meta?: unknown;
}): number | undefined {
  const meta = response.meta as { revision?: unknown } | null | undefined;
  const fromMeta = readCvRevision(meta?.revision);
  if (fromMeta !== undefined) return fromMeta;
  const data = response.data as
    | { revision?: unknown; cv?: { revision?: unknown } | null }
    | null
    | undefined;
  if (!data || typeof data !== 'object') return undefined;
  return readCvRevision(data.revision) ?? readCvRevision(data.cv?.revision);
}

/** `If-Match` for a write based on `revision`, or nothing (old request). */
export function cvIfMatchHeaders(
  revision: number | undefined,
): Record<string, string> | undefined {
  return revision === undefined ? undefined : { 'If-Match': `"${revision}"` };
}

/** Optional write options every CV write function accepts. */
export interface CvWriteOptions {
  /** The revision the write is based on; sent as `If-Match`. */
  ifMatch?: number;
  /** Receives the revision the server stored. */
  onRevision?: (revision: number) => void;
}

/**
 * Request options for a CV write: `{ headers: { 'If-Match' } }` when a
 * revision is stated, otherwise none, so the request stays exactly as before.
 */
export function cvWriteRequestArgs(
  write: CvWriteOptions | undefined,
): [] | [{ headers: Record<string, string> }] {
  const headers = cvIfMatchHeaders(write?.ifMatch);
  return headers ? [{ headers }] : [];
}

/**
 * The trailing write-options argument for a CV write function: none when the
 * caller states nothing (the call stays exactly as before).
 */
export function cvWriteArgs(write: CvWriteOptions | undefined): [] | [CvWriteOptions] {
  return write ? [write] : [];
}

/**
 * Finish a CV write response: throw (a typed conflict for a stale write,
 * otherwise the server message as before) or report the stored revision and
 * return the data.
 */
export function settleCvWriteResponse<T>(
  response: {
    data: T | null;
    error?: { code?: string; message: string } | null;
    meta?: unknown;
  },
  write: CvWriteOptions | undefined,
): T | null {
  if (response.error) {
    throw cvConflictFromResponse(response) ?? new Error(response.error.message);
  }
  const revision = readResponseRevision(response);
  if (revision !== undefined) write?.onRevision?.(revision);
  return response.data;
}

/** The conflict a failed CV write response stands for, else null. */
export function cvConflictFromResponse(response: {
  error?: { code?: string; message?: string } | null;
  meta?: unknown;
}): CvRevisionConflictError | null {
  if (response.error?.code !== CV_REVISION_CONFLICT_CODE) return null;
  const meta = response.meta as { currentRevision?: unknown } | null | undefined;
  return new CvRevisionConflictError(
    response.error.message || 'This CV changed in another place.',
    readCvRevision(meta?.currentRevision),
  );
}

/**
 * The CV a write target names, or null for the main profile (`null`, '' or
 * 'master'), which is not a CV row and is never revision-guarded.
 */
export function cvRevisionTarget(target: string | null | undefined): string | null {
  const normalized = typeof target === 'string' ? target.trim() : '';
  if (!normalized || normalized === 'master') return null;
  return normalized;
}

// ─── Read observations (reported by the API layer) ───────────────────

type ReadListener = (target: string, revision: number) => void;
const readListeners = new Set<ReadListener>();

/** Called by the CV read functions with the revision the server returned. */
export function reportCvReadRevision(
  target: string | null | undefined,
  revision: number | undefined,
): void {
  const cv = cvRevisionTarget(target);
  if (!cv || revision === undefined) return;
  for (const listener of readListeners) listener(cv, revision);
}

export function subscribeCvReadRevisions(listener: ReadListener): () => void {
  readListeners.add(listener);
  return () => {
    readListeners.delete(listener);
  };
}

// ─── Coordinator ─────────────────────────────────────────────────────

/** The base revision of one open draft (a sheet or edit page). */
export interface CvRevisionLease {
  readonly target: string;
  revision: number | undefined;
}

export interface CvWriteSent<T> {
  value: T;
  /** The revision the server stored, when it said. */
  revision?: number;
}

export interface CvWriteTask<T> {
  kind: CvWriteKind;
  /** A newer pending task with the same key replaces this one before it is sent. */
  coalesceKey?: string;
  /** The draft this write comes from; none = state the latest known revision. */
  lease?: CvRevisionLease | null;
  send(ifMatch: number | undefined): Promise<CvWriteSent<T>>;
}

/** Where the latest known revision per CV lives (the query cache in the app). */
export interface CvRevisionSource {
  get(target: string): number | undefined;
  /** Record a revision; the source keeps the highest it has seen. */
  set(target: string, revision: number): void;
}

export interface CvWriteCoordinatorDeps {
  revisions: CvRevisionSource;
  /** Refetch the CV so the editor shows what the other device saved. */
  refresh(target: string): Promise<unknown>;
  /** Ask the user once; must resolve (a dismissal is 'keep'). */
  prompt(kind: CvWriteKind): Promise<CvConflictChoice>;
  /** Upper bound on waiting for the refetch before asking. Default 8 s. */
  refreshTimeoutMs?: number;
}

interface Settler {
  resolve(value: unknown): void;
  reject(error: unknown): void;
}

interface Entry {
  task: CvWriteTask<unknown>;
  settlers: Settler[];
}

interface Lane {
  running: boolean;
  pending: Entry[];
  leases: Set<CvRevisionLease>;
}

const DEFAULT_REFRESH_TIMEOUT_MS = 8_000;

export class CvWriteCoordinator {
  private readonly lanes = new Map<string, Lane>();

  constructor(private readonly deps: CvWriteCoordinatorDeps) {
    subscribeCvReadRevisions((target, revision) => this.observe(target, revision));
  }

  /** The latest revision this client has seen for a CV. */
  latest(target: string): number | undefined {
    return this.deps.revisions.get(target);
  }

  /**
   * A revision read from the server. Moves the latest known revision forward;
   * a draft opened before any revision was known adopts it (that is what the
   * draft was filled from). It never moves an existing draft's base.
   */
  observe(target: string, revision: number): void {
    if (readCvRevision(revision) === undefined) return;
    this.deps.revisions.set(target, revision);
    const lane = this.lanes.get(target);
    if (!lane) return;
    for (const lease of this.leasesOf(lane)) {
      if (lease.revision === undefined) lease.revision = revision;
    }
  }

  /** A draft based on the latest revision known now. Attach it to keep it current. */
  createLease(target: string): CvRevisionLease {
    return { target, revision: this.latest(target) };
  }

  attachLease(lease: CvRevisionLease): void {
    this.lane(lease.target).leases.add(lease);
  }

  detachLease(lease: CvRevisionLease): void {
    const lane = this.lanes.get(lease.target);
    if (!lane) return;
    lane.leases.delete(lease);
    this.dropIdleLane(lease.target, lane);
  }

  /** Queue one write for a CV; resolves with its value or rejects. */
  run<T>(target: string, task: CvWriteTask<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const lane = this.lane(target);
      const entry: Entry = {
        task: task as CvWriteTask<unknown>,
        settlers: [{ resolve: resolve as (value: unknown) => void, reject }],
      };
      if (task.coalesceKey) {
        const index = lane.pending.findIndex(
          (pending) => pending.task.coalesceKey === task.coalesceKey,
        );
        if (index >= 0) {
          // Newest pending wins: the older save settles with this one.
          const [older] = lane.pending.splice(index, 1);
          entry.settlers.push(...older.settlers);
        }
      }
      lane.pending.push(entry);
      void this.drain(target, lane);
    });
  }

  private lane(target: string): Lane {
    let lane = this.lanes.get(target);
    if (!lane) {
      lane = { running: false, pending: [], leases: new Set() };
      this.lanes.set(target, lane);
    }
    return lane;
  }

  private dropIdleLane(target: string, lane: Lane): void {
    if (!lane.running && lane.pending.length === 0 && lane.leases.size === 0) {
      this.lanes.delete(target);
    }
  }

  /** Open drafts plus drafts whose saves are still queued (their sheet may have closed). */
  private leasesOf(lane: Lane): Set<CvRevisionLease> {
    const leases = new Set(lane.leases);
    for (const entry of lane.pending) {
      if (entry.task.lease) leases.add(entry.task.lease);
    }
    return leases;
  }

  private baseFor(target: string, lease: CvRevisionLease | null | undefined) {
    return lease?.revision ?? this.latest(target);
  }

  private async drain(target: string, lane: Lane): Promise<void> {
    if (lane.running) return;
    lane.running = true;
    try {
      while (lane.pending.length > 0) {
        const entry = lane.pending.shift()!;
        try {
          await this.process(target, lane, entry);
        } catch (error) {
          rejectEntry(entry, error);
        }
      }
    } finally {
      lane.running = false;
      this.dropIdleLane(target, lane);
    }
  }

  private async process(target: string, lane: Lane, entry: Entry): Promise<void> {
    const { task } = entry;
    const lease = task.lease ?? null;
    let base = this.baseFor(target, lease);
    for (;;) {
      let sent: CvWriteSent<unknown>;
      try {
        sent = await task.send(base);
      } catch (error) {
        if (!isCvRevisionConflict(error)) {
          rejectEntry(entry, error);
          return;
        }
        if (error.currentRevision !== undefined) {
          this.deps.revisions.set(target, error.currentRevision);
        }
        await this.refreshBounded(target);
        const choice = await this.ask(task.kind);
        if (choice === 'apply') {
          // The user rebased this draft onto what is saved now: the same
          // patch is resent, stating the new revision.
          const next = this.latest(target) ?? error.currentRevision;
          this.rebaseDraft(lane, lease, base, next);
          base = next;
          continue;
        }
        const notApplied = new CvChangeNotAppliedError(
          choice === 'discard' ? 'discarded' : 'kept',
        );
        rejectEntry(entry, notApplied);
        this.settleSameDraft(lane, lease, base, notApplied);
        return;
      }
      if (sent.revision !== undefined) {
        this.recordOwnWrite(target, lane, base, sent.revision, lease);
      }
      resolveEntry(entry, sent.value);
      return;
    }
  }

  /**
   * This client stored `revision` on top of `base`. Every draft that was
   * based on `base` (or knew nothing yet) has seen everything in between,
   * because it was this client's own change.
   */
  private recordOwnWrite(
    target: string,
    lane: Lane,
    base: number | undefined,
    revision: number,
    lease: CvRevisionLease | null,
  ): void {
    this.deps.revisions.set(target, revision);
    for (const open of this.leasesOf(lane)) {
      if (open.revision === base || open.revision === undefined) {
        open.revision = revision;
      }
    }
    if (lease) lease.revision = revision;
  }

  /**
   * "Apply my change on top": the conflicting draft, and the rest of the
   * same save still queued behind it (same stale base), move to `next`.
   * Other open drafts are not touched: they have not been asked.
   */
  private rebaseDraft(
    lane: Lane,
    lease: CvRevisionLease | null,
    stale: number | undefined,
    next: number | undefined,
  ): void {
    // A save without a draft (style panel) rebases nothing else.
    if (!lease) return;
    lease.revision = next;
    for (const entry of lane.pending) {
      const pendingLease = entry.task.lease;
      if (pendingLease && pendingLease.revision === stale) {
        pendingLease.revision = next;
      }
    }
  }

  /** "Discard" / "keep editing" also answers the rest of the same save. */
  private settleSameDraft(
    lane: Lane,
    lease: CvRevisionLease | null,
    stale: number | undefined,
    error: CvChangeNotAppliedError,
  ): void {
    if (!lease) return;
    const remaining: Entry[] = [];
    for (const entry of lane.pending) {
      const pendingLease = entry.task.lease;
      if (pendingLease && (pendingLease === lease || pendingLease.revision === stale)) {
        rejectEntry(entry, error);
      } else {
        remaining.push(entry);
      }
    }
    lane.pending = remaining;
  }

  private async refreshBounded(target: string): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, this.deps.refreshTimeoutMs ?? DEFAULT_REFRESH_TIMEOUT_MS);
    });
    try {
      await Promise.race([
        Promise.resolve()
          .then(() => this.deps.refresh(target))
          .then(
            () => undefined,
            () => undefined,
          ),
        timeout,
      ]);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }

  private async ask(kind: CvWriteKind): Promise<CvConflictChoice> {
    try {
      const choice = await this.deps.prompt(kind);
      return choice === 'apply' || choice === 'discard' ? choice : 'keep';
    } catch {
      // A prompt that cannot be shown never turns into an overwrite.
      return 'keep';
    }
  }
}

function resolveEntry(entry: Entry, value: unknown): void {
  for (const settler of entry.settlers) settler.resolve(value);
}

function rejectEntry(entry: Entry, error: unknown): void {
  for (const settler of entry.settlers) settler.reject(error);
}
